import React, { useEffect } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import { DPButton, DPDialog } from "Components/DP";
import { DPOperatingManager, OperatingManagerFormFields } from "Shared/types/OperatingManager";
import { permissionLabels } from "Shared/types/Admins";
import pretty from "Shared/pretty";
import { FormProvider, useForm } from "react-hook-form";
import { OperatingManagerDetails } from "../OperatingManagerDetails/OperatingManagerDetails";
import useStyles from "./styles";
import { useMutation } from "@apollo/client";
import { UpdateEmployerRewardBudgetMutation, UpdateEmployerRewardBudgetMutationVariables } from "generated/graphql";
import mutations from "Shared/mutations";
import { handleValidationErrors } from "Modules/HandleValidationErrors";
import { successToast } from "Modules/DPToast";
import moneyToCents from "Shared/money";
import { OperatingManagerPermissions } from "../OperatingManagerPermissions/OperatingManagerPermissions";

function mapManagerToFormFields(manager: DPOperatingManager): OperatingManagerFormFields {
  const { employer, expectedApprovers, totalAmountCents } = manager;

  return {
    firstName: employer.firstName!,
    lastName: employer.lastName!,
    email: employer.email!,
    phoneNumber: employer.workPhoneNumber!,
    permissions: { value: employer.permissions, label: permissionLabels[employer.permissions] },
    budget: (totalAmountCents / 100 ).toString(),
    locations: employer.associatedLocations.items.map(location => ({
      value: location.id,
      label: location.name || "",
    })),
    secondaryApprovers:  (expectedApprovers || []).map(approver => {
      return {
        value: approver!.id,
        label: pretty.fullName(approver!.firstName!, approver!.lastName!),
      };
    }),
  };
}

export interface EditOperatingManagerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  manager: DPOperatingManager;
}

export function EditOperatingManagerDialog(props: EditOperatingManagerDialogProps) {
  const classes = useStyles();
  const formMethods = useForm<OperatingManagerFormFields>({
    mode: "onBlur",
  });
  const { register, getValues, trigger } = formMethods;

  const [updateRewardBudgetFn] = useMutation<UpdateEmployerRewardBudgetMutation, UpdateEmployerRewardBudgetMutationVariables>(
    mutations.UPDATE_REWARD_BUDGET,
  );

  useEffect(() => {
    register({ name: "locations" });
  }, [register]);

  if (!props.manager) return <></>;

  async function onSubmit() {
    const formData = getValues();
    const isFormValid = await trigger();

    if (!isFormValid) {
      return;
    }

    try {
      await updateRewardBudgetFn({
        variables: {
          updateEmployerRewardBudgetInput: {
            expectedApproverIds: formData.secondaryApprovers?.map(approver => approver.value),
            employerId: props.manager.employer.id!,
            totalAmountCents: moneyToCents(formData.budget),
          },
        },
      });
      successToast("Operating Manager successfully edited.", false);
      props.onClose();
    } catch (error) {
      handleValidationErrors(error);
    }
  }

  const formFields = mapManagerToFormFields(props.manager);

  return (
    <DPDialog open={props.isOpen}>
      <FormProvider {...formMethods}>
        <Box m={4} className={classes.formFieldsBox}>
          <Typography variant="h2" className={classes.title}>
            Edit Operating Manager
          </Typography>
          <OperatingManagerDetails
            formFields={formFields}
            shouldDisableFields={true}
          />
          <OperatingManagerPermissions
            formFields={formFields}
            shouldDisableAdminFields={true}
          />
          <Box m={4}>
            <Grid container justify="center">
              <DPButton color="default" align="right" onClick={props.onClose}>
                Cancel
              </DPButton>
              <DPButton
                id="submit-btn"
                type={"submit"}
                align="right"
                onClick={onSubmit}
              >
                Submit
              </DPButton>
            </Grid>
          </Box>
        </Box>
      </FormProvider>
    </DPDialog>
  );
}
